<template>
  <div class="cb-navigator-presentation">
    <div v-if="renderedNavigationItems.length" class="mt-4">
      <DynamicScroller
        ref="dynamicScrollerRef"
        class="pr-1"
        emit-update
        :items="renderedNavigationItems"
        key-field="renderKey"
        :min-item-size="24"
        :style="{ maxHeight }"
        @update="
          (viewStartIndex: number, viewEndIndex: number, visibleStartIndex: number, visibleEndIndex: number) => {
            visibleItemIndexes = {
              start: visibleStartIndex,
              end: visibleEndIndex,
            }
          }
        "
      >
        <template #default="{ item: n, index, active }">
          <DynamicScrollerItem :active="active" :index="index" :item="n">
            <div v-if="n">
              <div
                v-if="n.renderExtra?.type === 'heading' || n.renderExtra?.type === 'both'"
                class="h-7 pt-3 text-xs tracking-wide uppercase relative top-2 left-1 text-truncate"
                :style="{ color: themeVars.primary3 }"
                :title="n.renderExtra?.name"
              >
                {{ n.renderExtra?.name }}
              </div>
              <div
                v-if="n.type === 'PageBreak'"
                class="min-h-7 pt-3 text-xs tracking-wide uppercase left-1 top-2 overflow-visible"
                :style="{ color: themeVars.primary3 }"
                :title="t('canvasUI.canvasBuilder.components.titles.pageBreak')"
              >
                {{
                  n.name !== ComponentTypes.PageBreak ? n.name : t('canvasUI.canvasBuilder.components.titles.pageBreak')
                }}
              </div>
              <!-- skip page break elements -->
              <template v-if="n.type === 'PageBreak'" />
              <div v-else-if="n.name" class="pt-3">
                <div
                  class="cb-navigator-presentation__item pa-3 cursor-pointer rounded-2"
                  :class="getClass(n.id, n.sectionId, isTocEnhancementsEnabled ? n.sectionIndex : undefined)"
                  @click="goTo(n)"
                >
                  <div class="flex items-center gap-1 font-bold">
                    <CIcon
                      v-if="isHomePage(n.id, n.sectionId)"
                      class="mr-1 cursor-pointer color-primary"
                      icon="home"
                      size="14"
                    />
                    <span v-if="!pitcherSettings.canvas?.disable_display_index_in_toc">
                      {{ n.displayIndex + 1 }}.
                    </span>
                    <span
                      class="leading-5"
                      :class="{
                        'line-clamp-3': n.type === 'Section',
                        'text-truncate': n.type !== 'Section',
                      }"
                    >
                      {{ n.name }}
                    </span>
                  </div>
                  <CImage
                    v-if="n.type === 'Section' && mode === 'thumbnails'"
                    class="min-w-[100%] w-full h-auto rounded-2"
                    :img-props="{ class: 'w-full mt-2' }"
                    loading="lazy"
                    object-fit="cover"
                    preview-disabled
                    :src="n?.thumbnailUrl"
                    :width="100"
                  />
                </div>
              </div>
              <div v-else class="pt-3">
                <div
                  class="cb-navigator-presentation__item h-[59] pa-3 cursor-pointer rounded-2"
                  :class="getClass(n.id, n.sectionId, isTocEnhancementsEnabled ? n.sectionIndex : undefined)"
                  @click="goTo(n)"
                >
                  <div class="flex items-center gap-1 font-italic" :style="{ color: themeVars.text2 }">
                    <span v-if="!pitcherSettings.canvas?.disable_display_index_in_toc">
                      {{ n.displayIndex + 1 }}.
                    </span>
                    <span
                      class="leading-5"
                      :class="{
                        'line-clamp-3': n.type === 'Section',
                        'text-truncate': n.type !== 'Section',
                      }"
                    >
                      {{ t('canvasUI.canvasBuilder.navigator.noSectionsAdded') }}
                    </span>
                    <CIcon
                      v-if="isHomePage(n.id, n.sectionId)"
                      class="ml-1 cursor-pointer"
                      color="primary"
                      icon="home"
                      size="14"
                    />
                  </div>
                </div>
              </div>
              <div
                v-if="n.renderExtra?.type === 'separator' || n.renderExtra?.type === 'both'"
                class="h-4 mt-3 mx-16 flex items-start border-solid border-b-0 border-l-0 border-r-0 border-gray-200"
                :class="{
                  'pa-3': mode === 'thumbnails',
                }"
              >
                &nbsp;
              </div>
            </div>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
    </div>
    <div v-else class="mt-3 w-full text-center text-truncate" :style="{ color: themeVars.primary3 }">
      {{
        search
          ? t('canvasUI.canvasBuilder.navigatorPresentation.noResults')
          : t('canvasUI.canvasBuilder.navigatorPresentation.addSomeContent')
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'
import { computed, inject, onBeforeUnmount, onMounted, ref, type ComputedRef, type UnwrapRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

import { ComponentTypes, type ComponentNode, type SectionListProps } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useSectionNavigation from '@canvas-builder/composables/useSectionNavigation'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useContentSelector from '@canvas-builder/composables/useContentSelector'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import { getComponentTitle, getNodeDisplayNameByComponentType } from '@canvas-builder/util/canvas.util'
import useCollapsibles from '@canvas-builder/composables/useCollapsibles'
import CImage from '@lib/components/CImage/CImage.vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { type OrganizationSettings } from '@lib/types/organizationSettings.types'
import { type InstanceSettings } from '@lib/types/instanceSettings.types'
import CIcon from '@lib/components/CIcon/CIcon.vue'

const props = withDefaults(
  defineProps<{ search?: string; mode: 'thumbnails' | 'list'; isFullscreenNavigator?: boolean }>(),
  {
    isFullscreenNavigator: false,
    search: '',
  },
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const { t } = useI18n()

const {
  navigationItems,
  activeNavigationItems,
  contentRef,
  componentNodesById,
  isEditMode,
  isFullscreen,
  componentSettingsMode,
  addMode,
  componentEditMode,
  componentSelectedMode,
  mode: canvasMode,
  canvasHomePage,
  visibleCollectionPlayerRefs,
  isCanvasPreviewMode,
  setAddMode,
  exitFullscreen,
  setComponentEditMode,
  setComponentSelectedMode,
  setComponentSettingsMode,
  activateNavigationItem,
} = useCanvas()
const { isEditingCanvasTheme } = useCanvasTheme()
const { clearContentSelectorMode, contentSelectorMode } = useContentSelector()
const maxHeight = computed(() => {
  if (isFullscreen.value) return `calc(100vh - ${250}px)`

  const scrollerMaxHeightOffset = !isFullscreen.value && canvasMode.value !== 'sharing' ? 270 : 154
  return `calc(100vh - ${scrollerMaxHeightOffset}px)`
})
const { navigateTo, activeIndex } = useSectionNavigation(contentRef, navigationItems, activeNavigationItems, isEditMode)
const { goToPage, goToNextPage, goToPreviousPage, currentPageIndex, currentSectionList, canvasPages } = useCanvasPages()

const themeVars = useThemeVars()
const collapsiblesStore = useCollapsibles()
const dynamicScrollerRef = ref<typeof DynamicScroller>()
const visibleItemIndexes = ref<{ start: number; end: number }>({ start: 0, end: 0 })

// Computed property for the feature flag to avoid repeated checks
const isTocEnhancementsEnabled = computed(
  () => !!launchDarkly.value.enable_toc_enhancements_to_support_same_section_in_section_list,
)

const scrollerIndex = computed(
  () =>
    dynamicScrollerRef.value?.items?.findIndex(
      (item: UnwrapRef<typeof renderedNavigationItems>[number]) => item.index === activeIndex.value,
    ) ?? -1,
)

// Helper to calculate which occurrence (0, 1, 2...) a section is among duplicates with the same sectionId
// targetIndex can be either:
// - sectionIndex (position in section list sections array) for edit mode
// - index (position in filteredNavigationItems array) for presentation mode
function getSectionOccurrenceIndex(sectionId: string, targetIndex: number): number {
  const sectionsWithSameId = filteredNavigationItems.value.filter(
    (item) => item.type === 'Section' && item.sectionId === sectionId,
  )
  // Try matching by sectionIndex first (edit mode)
  let occurrenceIndex = sectionsWithSameId.findIndex((item) => item.sectionIndex === targetIndex)
  // If not found, try matching by index (presentation mode)
  if (occurrenceIndex === -1) {
    occurrenceIndex = sectionsWithSameId.findIndex((item) => item.index === targetIndex)
  }
  return occurrenceIndex
}

function searchMatchesMetadata(metadata?: Record<string, any>, searchTerm: string = '') {
  if (!metadata || !searchTerm) return false

  function search(value: any): boolean {
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString().toLowerCase().includes(searchTerm)
    } else if (Array.isArray(value)) {
      return value.some((item) => search(item))
    } else if (typeof value === 'object' && value !== null) {
      return Object.values(value).some((subValue) => search(subValue))
    }
    return false
  }

  return search(metadata)
}

const filteredNavigationItems = computed(() => {
  let displayIndex = 0
  let navItemsWithIdx = navigationItems.value.map((n, index) => {
    // Only increment displayIndex for non-PageBreak items
    const currentDisplayIndex = n.type === 'PageBreak' ? displayIndex : displayIndex++
    return {
      ...n,
      index,
      displayIndex: currentDisplayIndex,
      name:
        n.type === 'Section'
          ? n.name +
            (n.pageIndex !== undefined && n.pageIndex > 0 ? ` - ${t('canvasUI.common.page')} ${n.pageIndex + 1}` : '')
          : getNodeDisplayNameByComponentType(componentNodesById.value[n.id] as ComponentNode) ||
            getComponentTitle(n.type, t),
    }
  })

  if (pitcherSettings.value.canvas?.hide_page_break_in_toc) {
    navItemsWithIdx = navItemsWithIdx.filter((n) => n.type !== 'PageBreak')
  }

  if (!props.search) {
    return navItemsWithIdx
  }

  return navItemsWithIdx.filter((n) => {
    const name = n.name.toLowerCase()
    const type = n.type.toLowerCase()
    const search = props.search.toLowerCase()

    return name.includes(search) || type.includes(search) || searchMatchesMetadata(n.metadata, search)
  })
})

const renderedNavigationItems = computed(() => {
  const items = cloneDeep(filteredNavigationItems.value)
  const sectionListIds = new Set(...[items.filter((item) => item.type === 'Section').map((item) => item.id)])

  const extraInfoBySectionListId: Record<string, { name: string; type: 'heading' | 'separator' | 'both' }> = {}

  // Process section lists and their sections
  sectionListIds.forEach((sectionListId) => {
    const node = componentNodesById.value[sectionListId] as ComponentNode<SectionListProps>
    if (!node || node.tracking_id === node.type) return

    const sections = node?.data?.sections
    if (!sections?.length) {
      extraInfoBySectionListId[sectionListId] = {
        name: node.tracking_id,
        type: 'both' as const,
      }
      return
    }

    // For each section, determine if it's first or last
    const firstId = sections[0].id
    const lastId = sections[sections.length - 1].id

    // Set first section extra info (heading)
    if (firstId) {
      extraInfoBySectionListId[`${sectionListId}-${firstId}`] = {
        name: node.tracking_id,
        type: 'heading' as const,
      }
    }

    // Set last section extra info (separator)
    if (lastId) {
      extraInfoBySectionListId[`${sectionListId}-${lastId}`] = {
        name: node.tracking_id,
        type: 'separator' as const,
      }
    }

    // Handle case where there's only one section
    if (sections.length === 1 && firstId) {
      extraInfoBySectionListId[`${sectionListId}-${firstId}`] = {
        name: node.tracking_id,
        type: 'both' as const,
      }
    }
  })

  // Track section list names we've already displayed
  const usedHeadings = new Set<string>()

  return items.map((item) => {
    // Generate unique ID and render key
    let uniqueId = item.id
    const renderKey = item.index !== undefined ? `${uniqueId}-${item.index}` : uniqueId

    if (item.type === 'Section' && item.sectionId) {
      uniqueId = `${item.id}-${item.sectionId}`

      // Get heading name from component
      const sectionListComponent = componentNodesById.value[item.id] as ComponentNode<SectionListProps> | undefined
      const headingName = sectionListComponent?.tracking_id || ''

      // Get the extra info for this item
      const extraInfo = extraInfoBySectionListId[uniqueId]

      // Skip or modify headings we've already seen
      if (extraInfo && (extraInfo.type === 'heading' || extraInfo.type === 'both')) {
        if (usedHeadings.has(headingName)) {
          // Already displayed this heading
          if (extraInfo.type === 'both') {
            // Convert 'both' to just 'separator'
            return {
              ...item,
              renderKey,
              renderExtra: { ...extraInfo, type: 'separator' as const },
            }
          } else {
            // Skip heading entirely
            return {
              ...item,
              renderKey,
              renderExtra: null,
            }
          }
        } else {
          // First time seeing this heading
          usedHeadings.add(headingName)
        }
      }

      return {
        ...item,
        renderKey,
        renderExtra: extraInfo,
      }
    }

    return {
      ...item,
      renderKey,
      renderExtra: null,
    }
  })
})

function getClass(id: string, sectionId?: string, itemIndex?: number) {
  const enhancementsEnabled = isTocEnhancementsEnabled.value
  let isActive = false

  if (enhancementsEnabled) {
    // Both edit and presentation modes need to handle duplicate sections
    if (sectionId && itemIndex !== undefined) {
      const thisOccurrence = getSectionOccurrenceIndex(sectionId, itemIndex)

      isActive = activeNavigationItems.value.some((activeItem) => {
        if (activeItem.id !== id || activeItem.sectionId !== sectionId) return false

        // activeItem.index is the sectionIndex (position in section list) when set from visibility observers
        if (typeof activeItem.index === 'number') {
          const activeOccurrence = getSectionOccurrenceIndex(sectionId, activeItem.index)
          return activeOccurrence === thisOccurrence
        }

        // If no index, just match by id and sectionId
        return true
      })
    } else {
      isActive = !!activeNavigationItems.value.find((item) => item.id === id && item.sectionId === sectionId)
    }

    return isActive ? 'cb-navigator-presentation__item--active' : 'cb-navigator-presentation__item--inactive'
  }

  if (!isEditMode.value) {
    // Presentation mode
    const pageIndex = currentPageIndex.value
    const { sectionListId, sectionListIndex } = currentSectionList.value

    if (sectionListId && id === sectionListId) {
      // This is a section list item
      if (sectionId) {
        // This is a section within a section list
        const sectionListComponent = canvasPages.value[pageIndex]?.find?.(
          (component): component is ComponentNode<SectionListProps> =>
            component.id === sectionListId &&
            [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
        )

        if (sectionListComponent?.data?.sections) {
          // Get the current active section
          const currentSection = sectionListComponent.data.sections[sectionListIndex]

          if (currentSection?.id === sectionId) {
            // The current section ID matches this item's section ID

            // Only use complex duplicate handling logic when feature flag is enabled
            if (enhancementsEnabled) {
              // Count sections with this ID to determine occurrence position
              const sectionsWithSameId = sectionListComponent.data.sections.filter(
                (section) => section.id === sectionId,
              )

              // Find the position of current section among others with same ID
              const currentPositionAmongDuplicates =
                sectionListComponent.data.sections
                  .slice(0, sectionListIndex + 1)
                  .filter((section) => section.id === sectionId).length - 1

              // If there are multiple sections with this ID
              if (sectionsWithSameId.length > 1) {
                // Get all navigator items for this section ID
                const navItems = filteredNavigationItems.value.filter(
                  (item) => item.type === 'Section' && item.sectionId === sectionId,
                )

                // The nav item index should match the current position among duplicates
                isActive = navItems.findIndex((item) => item.index === itemIndex) === currentPositionAmongDuplicates
              } else {
                // For a single section with this ID, just match the section ID
                isActive = true
              }
            } else {
              // When feature flag is disabled, use simple section ID matching
              isActive = true
            }
          }
        }
      } else {
        // This is the section list container itself
        isActive = true
      }
    } else {
      // For non-section-list components
      isActive = !!canvasPages.value[pageIndex]?.some?.((component) => component.id === id)
    }
  } else {
    // Edit mode logic
    if (componentSelectedMode.value && componentSelectedMode.value.id === id) {
      if (sectionId) {
        if (enhancementsEnabled && itemIndex !== undefined) {
          const activeItem = renderedNavigationItems.value?.[scrollerIndex.value]
          isActive = activeItem?.sectionId === sectionId && activeItem?.sectionIndex === itemIndex
        } else {
          const activeItem = renderedNavigationItems.value?.[scrollerIndex.value]
          isActive = activeItem?.sectionId === sectionId
        }
      } else {
        isActive = true
      }
    }
  }

  return isActive ? 'cb-navigator-presentation__item--active' : 'cb-navigator-presentation__item--inactive'
}

function goTo(n: UnwrapRef<typeof renderedNavigationItems>[number], isCorrection?: boolean) {
  const enhancementsEnabled = isTocEnhancementsEnabled.value
  const isCollapsiblesMode = (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') === 'collapsibles'

  if (isEditMode.value) {
    // Edit mode: use sectionIndex for occurrence calculation
    const sectionOccurrenceIndex =
      enhancementsEnabled && n.type === 'Section' && n.sectionId && typeof n.sectionIndex === 'number'
        ? getSectionOccurrenceIndex(n.sectionId, n.sectionIndex)
        : undefined

    const navIndex = n.index
    const offset = n.type === 'Section' && n.renderExtra?.type === 'heading' ? -36 : 0

    if (enhancementsEnabled && n.type === 'Section' && n.sectionId && sectionOccurrenceIndex !== undefined) {
      navigateTo(n.index, sectionOccurrenceIndex, offset)
    } else {
      navigateTo(navIndex, 0, offset)
    }

    // Immediately activate the navigation item to ensure it's highlighted before visibility observers fire
    if (n.type === 'Section' && n.sectionId && typeof n.sectionIndex === 'number') {
      activateNavigationItem(n.id, n.sectionId, false, n.sectionIndex)
    }

    if ([ComponentTypes.SectionList, ComponentTypes.ContentList].some((type) => type == n.type)) {
      const firstSectionItem = filteredNavigationItems.value.find((item) => item.type === 'Section' && item.id === n.id)

      if (firstSectionItem) {
        setComponentSelectedMode(n.id, enhancementsEnabled ? firstSectionItem.index : undefined)
      } else {
        setComponentSelectedMode(n.id, enhancementsEnabled ? n.index : undefined)
      }
    } else {
      setComponentSelectedMode(n.id, enhancementsEnabled ? n.index : undefined)
    }

    if (isCollapsiblesMode && n.id && n.sectionId) {
      collapsiblesStore.setClickedOnSection(n.id, n.sectionId)
    }

    if (!isCorrection) {
      setTimeout(() => {
        goTo(n, true)
      }, 250)
    }
  } else {
    // Presentation mode: use n.index for occurrence calculation
    const sectionOccurrenceIndex =
      enhancementsEnabled && n.type === 'Section' && n.sectionId
        ? getSectionOccurrenceIndex(n.sectionId, n.index)
        : undefined

    activeIndex.value = n.index

    // Only clear and activate if we have valid activation parameters
    if (n.sectionId !== undefined && n.id) {
      // Use sectionIndex for sections (consistent with edit mode), otherwise use n.index
      const activationIndex =
        n.type === 'Section' && n.sectionId && typeof n.sectionIndex === 'number' ? n.sectionIndex : n.index

      // Ensure we have a valid activation index before clearing
      if (activationIndex !== undefined && activationIndex !== null) {
        activeNavigationItems.value = []
        activateNavigationItem(n.id, n.sectionId, true, activationIndex)
      }
    }

    if (isCollapsiblesMode) {
      goToPage({
        componentId: n.id,
        sectionId: n.sectionId,
        itemIndex: enhancementsEnabled && sectionOccurrenceIndex !== undefined ? sectionOccurrenceIndex : n.index,
      })

      setTimeout(
        () => {
          navigateTo(n.index)
        },
        n.sectionId ? 250 : 0, // only add a bit of delay if collapsible section item
      )

      if (n.id && n.sectionId) {
        collapsiblesStore.setClickedOnSection(n.id, n.sectionId)
      }
    } else {
      goToPage({
        componentId: n.id,
        scrollTo: 'top',
        sectionId: n.sectionId,
        itemIndex: enhancementsEnabled && sectionOccurrenceIndex !== undefined ? sectionOccurrenceIndex : n.index,
      })
    }
  }
}

function goToFirstResult() {
  if (!renderedNavigationItems.value.length) return
  const firstItem = renderedNavigationItems.value[0]
  if (!firstItem) {
    console.warn('No first item found')
    return
  }
  goTo(firstItem)
}

function isIndexInvalid(index: number) {
  return index < 0 || index >= navigationItems.value.length
}

function getIndex(offset = 0) {
  const index = activeIndex.value + offset

  if (isIndexInvalid(index)) {
    return null
  }

  return index
}

function onKeyDown(e: KeyboardEvent) {
  // Check for modal in current document and parent window
  const isImagePreviewOpen = document.querySelector('.n-image-preview-container')
  const isAnyModalOpen =
    document.querySelector('.n-modal-container') || window.parent.document.querySelector('.n-modal-container')

  if (isImagePreviewOpen || isAnyModalOpen) {
    return
  }
  if (isEditMode.value) {
    handleEditModeNavigation(e)
  } else {
    handlePresentationModeNavigation(e)
  }
}

function handleEditModeNavigation(e: KeyboardEvent, idx: number | null = null, isCorrection?: boolean) {
  if (e.key === 'Escape') {
    onEscape(e)
    return
  }

  const goUp = e.key === 'PageUp' || e.key === 'ArrowUp'
  const goDown = e.key === 'PageDown' || e.key === 'ArrowDown'

  if ((!goUp && !goDown) || isEventFromTextInput(e)) return
  e.preventDefault()

  if (idx === null) {
    if (goUp) idx = getIndex(-1)
    else if (goDown) idx = getIndex(1)
  }

  navigateTo(idx)

  if (goDown && scrollerIndex.value + 2 >= visibleItemIndexes.value.end)
    dynamicScrollerRef.value?.scrollToItem(scrollerIndex.value)

  if (goUp && scrollerIndex.value - 2 <= visibleItemIndexes.value.start)
    dynamicScrollerRef.value?.scrollToItem(scrollerIndex.value)

  if (!isCorrection) {
    setTimeout(() => {
      handleEditModeNavigation(e, idx, true)
    }, 250)
  }
}

function isEventFromTextInput(e: KeyboardEvent) {
  return e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement
}

function maybeCloseThings() {
  if (contentSelectorMode.value) {
    clearContentSelectorMode()
  } else if (componentSettingsMode.value) {
    setComponentSettingsMode(false)
  } else if (isEditingCanvasTheme.value) {
    isEditingCanvasTheme.value = false
  } else if (addMode.value) {
    setAddMode(false)
  } else if (componentEditMode.value) {
    setComponentEditMode(false)
  } else if (componentSelectedMode.value) {
    setComponentSelectedMode()
  } else {
    // ignore escape key for canvas builder close.
    // onExitClick()
  }
}

function onEscape(e: KeyboardEvent) {
  e.preventDefault()
  if (isFullscreen.value && !isCanvasPreviewMode.value) {
    exitFullscreen()
  } else if (canvasMode.value !== 'sharing') {
    maybeCloseThings()
  }
}

function findMostVisibleElement(elements: HTMLElement[]): HTMLElement | null {
  let mostVisibleElement = null
  let highestVisiblePercentage = 0
  let topPosition = Infinity

  const elementsArray = Array.from(elements)

  for (const element of elementsArray) {
    const rect = element.getBoundingClientRect()

    if (rect.width === 0 || rect.height === 0) continue

    const visibleTop = Math.max(0, rect.top)
    const visibleBottom = Math.min(window.innerHeight, rect.bottom)
    const visibleHeight = Math.max(0, visibleBottom - visibleTop)

    const visibleLeft = Math.max(0, rect.left)
    const visibleRight = Math.min(window.innerWidth, rect.right)
    const visibleWidth = Math.max(0, visibleRight - visibleLeft)

    const visibleArea = visibleWidth * visibleHeight
    const totalArea = rect.width * rect.height
    const visiblePercentage = visibleArea / totalArea

    const absoluteTopPosition = rect.top < 0 ? Math.abs(rect.bottom) : rect.top

    if (visiblePercentage > highestVisiblePercentage) {
      highestVisiblePercentage = visiblePercentage
      mostVisibleElement = element
      topPosition = absoluteTopPosition
    } else if (visiblePercentage === highestVisiblePercentage && absoluteTopPosition < topPosition) {
      mostVisibleElement = element
      topPosition = absoluteTopPosition
    }
  }

  return mostVisibleElement
}

function handlePresentationModeNavigation(e: KeyboardEvent) {
  if (isEventFromTextInput(e)) {
    return
  }

  if (e.shiftKey) {
    const collectionPlayers = Array.from(visibleCollectionPlayerRefs.value)
    const topElement = findMostVisibleElement(
      collectionPlayers.map((_ref) => _ref.value?.element).filter((element) => !!element),
    )

    if (!topElement) {
      return
    }

    const topRef = collectionPlayers.find((_ref) => _ref.value?.element === topElement)?.value

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        topRef?.handleKeyAction('right')
        break
      case 'ArrowLeft':
        e.preventDefault()
        topRef?.handleKeyAction('left')
        break
      case 'ArrowDown':
        e.preventDefault()
        topRef?.handleKeyAction('down')
        break
      case 'ArrowUp':
        e.preventDefault()
        topRef?.handleKeyAction('up')
        break
    }

    return
  }

  switch (e.key) {
    case 'ArrowRight':
    case 'PageDown':
      e.preventDefault()
      goToNextPage()
      break
    case 'ArrowLeft':
    case 'PageUp':
      e.preventDefault()
      goToPreviousPage()
      break
    case 'Escape':
      onEscape(e)

      break
  }
}

function isHomePage(id: string, sectionId?: string): boolean {
  return (
    canvasHomePage.value?.component_id === id &&
    (sectionId === undefined || canvasHomePage.value?.section_id === sectionId)
  )
}

watch(
  () => props.mode,
  () => {
    // keep the active item in the view when switching modes
    if (props.mode === 'thumbnails' && scrollerIndex.value > -1) {
      setTimeout(() => {
        dynamicScrollerRef.value?.scrollToItem(scrollerIndex.value)
      }, 100)
    }
  },
)

onMounted(() => {
  if (props.isFullscreenNavigator) return
  window.addEventListener('keydown', onKeyDown)
  window.goToCanvasPage = (sectionListId: string, sectionId: string, itemIndex: number) => {
    goToPage({ componentId: sectionListId, sectionId, itemIndex })
  }
})

onBeforeUnmount(() => {
  if (props.isFullscreenNavigator) return
  window.removeEventListener('keydown', onKeyDown)
  window.goToCanvasPage = undefined
})

defineExpose({ goToFirstResult })
</script>

<style lang="scss" scoped>
.cb-navigator-presentation__item {
  border: 1px solid v-bind('themeVars.primary5');

  &--active {
    background-color: v-bind('themeVars.primary4');
  }

  &--in-viewport {
    background-color: v-bind('themeVars.primary5');
  }

  &--inactive {
    background-color: v-bind('themeVars.base');
  }
}
</style>
